import type { RootState } from "@/app/store";
import { blobToBase64 } from "@/lib/blob-utils";
import { db } from "@/lib/db";
import { storeLayerStates } from "@/lib/layer-state";
import type { CreateLayerListParams, LayerState } from "@/types";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const createLayerList = createAsyncThunk<LayerState[], CreateLayerListParams>(
    'layerList/createLayerList',
    async ({ layers, thumbnailTable }) => {
        if (await db.layerState.count() > 0) {
            return db.layerState.toArray();
        }
        const [thumbnails, attachments] = await Promise.all([
            thumbnailTable.queryFeatures({ where: '1=1', outFields: ['*'] }),
            thumbnailTable.queryAttachments({ where: '1=1', returnMetadata: true }),
        ]);

        const layerStates: Omit<LayerState, 'id'>[] = [];
        for (const layer of layers) {
            if (layer.type !== 'group' || layer.title === '') continue;
            const group = layer as GroupLayer;
            for (const subLayer of group.layers) {
                const thumbnail = thumbnails.features.find((feature) => feature.attributes?.WebMapLayerName === subLayer.title);
                const attachmentUrl = thumbnail ? attachments[thumbnail.attributes.OBJECTID]?.[0]?.url : undefined;
                const image = attachmentUrl ? await fetch(`${attachmentUrl}?token=${import.meta.env.VITE_ESRI_API_KEY}`).then(response => response.blob()) : null;
                layerStates.push({
                    layerId: subLayer.id,
                    group: group.title ?? '',
                    title: subLayer.title ?? '',
                    visible: false,
                    legendInfos: [],
                    imgSrc: image ? await blobToBase64(image) : ''
                });
            }
        }
        return storeLayerStates(layerStates);
    },
    // Skip a rebuild while one is already fetching thumbnails.
    { condition: (_, { getState }) => !(getState() as RootState).layerList.loading },
)

export interface LayerListState {
    layers: LayerState[],
    loading: boolean,
}

export const initialState: LayerListState = {
    layers: [],
    loading: false,
}

export const layerListSlice = createSlice({
    name: 'layerList',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(createLayerList.fulfilled, (state, action) => {
            state.layers = action.payload;
            state.loading = false;
        });
        builder.addCase(createLayerList.rejected, (state, action) => {
            console.error(action.error);
            state.layers = [];
            state.loading = false;
        });
        builder.addCase(createLayerList.pending, (state) => {
            state.loading = true;
        });
    }
});

export const layerList = (state: RootState) => state.layerList.layers;
export const layerListLoading = (state: RootState) => state.layerList.loading;

export default layerListSlice.reducer;
